import * as Y from 'yjs';
import { IndexeddbPersistence } from 'y-indexeddb';
import { WebsocketProvider } from 'y-websocket';
import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Symbol used as transaction origin when React dispatches a local change.
 * This allows Yjs observers to ignore self-triggered updates and prevent infinite re-render loops.
 */
export const REACT_ORIGIN = Symbol('REACT_ORIGIN');

/**
 * Sync status values compatible with UI indicators.
 */
export const SyncStatus = {
    DISCONNECTED: 'disconnected',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    SYNCING: 'syncing',
    SYNCED: 'synced',
    ERROR: 'error',
    OFFLINE: 'offline'
};

/**
 * Helper to determine default WebSocket URL matching the Shepherd Flask backend
 */
export function getDefaultWsUrl() {
    if (typeof window === 'undefined') return 'ws://localhost:80/ws/yjs';
    const isHttps = window.location.protocol === 'https:';
    const protocol = isHttps ? 'wss:' : 'ws:';
    const hostname = window.location.hostname || 'localhost';

    // When running React dev server on port 3000 or devMode, backend is on port 80
    if (window.devMode || window.location.port === '3000') {
        return `${protocol}//${hostname}:80/ws/yjs`;
    }

    const host = window.location.host || `${hostname}:80`;
    return `${protocol}//${host}/ws/yjs`;
}

// Ensure window.Yjs global namespace exists in browser environment
if (typeof window !== 'undefined') {
    if (!window.Yjs) {
        window.Yjs = {
            tables: {},
            getTable: (tableName) => window.Yjs?.tables?.[tableName],
            initTable: (tableName, options) => initTable(tableName, options),
            destroyTable: (tableName) => destroyTable(tableName)
        };
    }
}

/**
 * Initializes a Yjs Document session for a given JSON database table.
 * Adheres strictly to the requirement:
 * window.Yjs.tables.<jsondb_name> contains { ydoc, indexeddbProvider, websocketProvider }
 */
export function initializeYjsDocumentSession(tableName, options = {}) {
    const {
        roomName = `shepherd-db-${tableName}`,
        websocketUrl = getDefaultWsUrl(),
        uid = null,
        enableIndexedDB = true,
        enableWebsocket = true,
        onIndexedDBHydrated,
        onWebsocketSynced,
        onConnectionStatusChange
    } = options;

    // 1. Create real In-Memory Y.Doc Instance
    const ydoc = new Y.Doc();

    const status = {
        isLocalHydrated: false,
        isNetworkConnected: false,
        isNetworkSynced: false,
        connectionStatus: 'disconnected'
    };

    let indexeddbProvider = null;
    let websocketProvider = null;

    // 2. Setup Local IndexedDB Persistence (Offline-first storage)
    if (enableIndexedDB && typeof window !== 'undefined') {
        indexeddbProvider = new IndexeddbPersistence(roomName, ydoc);

        indexeddbProvider.whenSynced.then(() => {
            status.isLocalHydrated = true;
            if (onIndexedDBHydrated) onIndexedDBHydrated();
        });
    }

    // 3. Setup WebSocket Provider (Native /ws/yjs/<room> endpoint on backend)
    if (enableWebsocket && websocketUrl) {
        websocketProvider = new WebsocketProvider(websocketUrl, roomName, ydoc, {
            connect: true,
            params: uid ? { uid: String(uid) } : {}
        });

        websocketProvider.on('status', (event) => {
            status.connectionStatus = event.status;
            status.isNetworkConnected = event.status === 'connected';
            if (onConnectionStatusChange) onConnectionStatusChange(event.status);
        });

        websocketProvider.on('sync', (isSynced) => {
            status.isNetworkSynced = isSynced;
            if (onWebsocketSynced) onWebsocketSynced(isSynced);
        });
    }

    // 4. Atomic Cleanup Teardown Function
    const destroy = () => {
        if (websocketProvider) {
            websocketProvider.disconnect();
            websocketProvider.destroy();
        }
        if (indexeddbProvider) {
            indexeddbProvider.destroy();
        }
        ydoc.destroy();
        if (typeof window !== 'undefined' && window.Yjs?.tables) {
            delete window.Yjs.tables[tableName];
        }
    };

    const instance = {
        tableName,
        roomName,
        ydoc,
        indexeddbProvider,
        websocketProvider,
        status,
        pendingChanges: false,
        destroy
    };

    // Track pending offline changes
    ydoc.on('update', (_update, origin) => {
        if (origin === 'remote' || origin === indexeddbProvider) return;
        const isOnline = navigator.onLine && websocketProvider?.wsconnected;
        if (!isOnline) {
            instance.pendingChanges = true;
        }
    });

    if (typeof window !== 'undefined') {
        if (!window.Yjs) {
            window.Yjs = {
                tables: {},
                getTable: (name) => window.Yjs?.tables?.[name],
                initTable: (name, opts) => initTable(name, opts),
                destroyTable: (name) => destroyTable(name)
            };
        }
        window.Yjs.tables[tableName] = instance;
    }

    return instance;
}

/**
 * Retrieves or lazily creates a table session instance.
 */
export function initTable(tableName, options = {}) {
    if (typeof window !== 'undefined' && window.Yjs?.tables?.[tableName]) {
        return window.Yjs.tables[tableName];
    }
    return initializeYjsDocumentSession(tableName, options);
}

/**
 * Gets an active table instance by table name.
 */
export function getTable(tableName) {
    if (typeof window === 'undefined' || !window.Yjs?.tables) return undefined;
    return window.Yjs.tables[tableName];
}

/**
 * Destroys an active table session.
 */
export function destroyTable(tableName) {
    const table = getTable(tableName);
    if (table) {
        table.destroy();
    }
}

// Backwards-compatible alias
export const loadYjsDocumentSession = initTable;

/**
 * ============================================================================
 * Custom React Hooks wrapping Yjs State
 * ============================================================================
 */

/**
 * Custom Hook: useYjsTable
 * Connects a component to a JSON database table session in window.Yjs.tables.
 */
export function useYjsTable(tableName, options = {}) {
    const [instance, setInstance] = useState(() => {
        if (!tableName) return null;
        return getTable(tableName) || null;
    });

    const [status, setStatus] = useState({
        isLocalHydrated: false,
        isNetworkConnected: false,
        isNetworkSynced: false,
        connectionStatus: 'disconnected'
    });

    useEffect(() => {
        if (!tableName) {
            setInstance(null);
            return;
        }

        const table = initTable(tableName, options);
        setInstance(table);
        setStatus({ ...table.status });

        const handleHydrated = () => {
            setStatus(prev => ({ ...prev, isLocalHydrated: true }));
        };

        const handleStatus = (event) => {
            setStatus(prev => ({
                ...prev,
                connectionStatus: event.status,
                isNetworkConnected: event.status === 'connected'
            }));
        };

        const handleSync = (isSynced) => {
            setStatus(prev => ({ ...prev, isNetworkSynced: isSynced }));
        };

        if (table.indexeddbProvider) {
            table.indexeddbProvider.whenSynced.then(handleHydrated);
        }

        if (table.websocketProvider) {
            table.websocketProvider.on('status', handleStatus);
            table.websocketProvider.on('sync', handleSync);
        }

        return () => {
            if (table.websocketProvider) {
                table.websocketProvider.off('status', handleStatus);
                table.websocketProvider.off('sync', handleSync);
            }
        };
    }, [tableName, options.roomName, options.websocketUrl, options.uid]);

    return {
        tableInstance: instance,
        ydoc: instance?.ydoc || null,
        indexeddbProvider: instance?.indexeddbProvider || null,
        websocketProvider: instance?.websocketProvider || null,
        status,
        destroy: () => {
            if (tableName) destroyTable(tableName);
        }
    };
}

/**
 * Custom Hook: useYjsDbGroups
 * Manages Database Groups via Yjs (persisted in IndexedDB, synced across users via WebSocket).
 * Provides custom hook API to create, delete, and list DB Groups.
 */
export function useYjsDbGroups(options = {}) {
    const [dbGroups, setDbGroups] = useState([]);
    const [isHydrated, setIsHydrated] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isSynced, setIsSynced] = useState(false);
    const instanceRef = useRef(null);

    useEffect(() => {
        const table = initTable('dbgroups', {
            roomName: options.roomName || 'shepherd-dbgroups',
            uid: options.uid,
            websocketUrl: options.websocketUrl
        });
        instanceRef.current = table;
        const ydoc = table.ydoc;
        const groupsMap = ydoc.getMap('dbgroups');

        const updateStateFromYjs = () => {
            const list = [];
            groupsMap.forEach((val) => {
                if (val && typeof val === 'object') {
                    list.push(val instanceof Y.Map ? yMapToObject(val) : val);
                }
            });
            setDbGroups(list);
        };

        const observer = (_events, transaction) => {
            if (transaction.origin === REACT_ORIGIN) return;
            updateStateFromYjs();
        };

        groupsMap.observe(observer);

        // Hydration from IndexedDB
        if (table.indexeddbProvider) {
            table.indexeddbProvider.whenSynced.then(() => {
                setIsHydrated(true);
                updateStateFromYjs();

                // If Yjs map is empty, seed from legacy ShepherdDB if it exists
                if (groupsMap.size === 0 && typeof indexedDB !== 'undefined') {
                    try {
                        const req = indexedDB.open('ShepherdDB');
                        req.onsuccess = (e) => {
                            const db = e.target.result;
                            if (db.objectStoreNames.contains('DBgroups')) {
                                const tx = db.transaction(['DBgroups'], 'readonly');
                                const store = tx.objectStore('DBgroups');
                                const getAll = store.getAll();
                                getAll.onsuccess = () => {
                                    const legacyList = getAll.result || [];
                                    if (legacyList.length > 0 && groupsMap.size === 0) {
                                        ydoc.transact(() => {
                                            legacyList.forEach((group) => {
                                                if (group && group.name) {
                                                    groupsMap.set(group.name, group);
                                                }
                                            });
                                        }, REACT_ORIGIN);
                                        updateStateFromYjs();
                                    }
                                };
                                tx.oncomplete = () => db.close();
                            } else {
                                db.close();
                            }
                        };
                    } catch (_) {}
                }
            });
        }

        const handleStatus = (event) => {
            setIsConnected(event.status === 'connected');
        };

        const handleSync = (synced) => {
            setIsSynced(synced);
            if (synced) {
                updateStateFromYjs();
            }
        };

        if (table.websocketProvider) {
            table.websocketProvider.on('status', handleStatus);
            table.websocketProvider.on('sync', handleSync);
        }

        updateStateFromYjs();

        return () => {
            groupsMap.unobserve(observer);
            if (table.websocketProvider) {
                table.websocketProvider.off('status', handleStatus);
                table.websocketProvider.off('sync', handleSync);
            }
        };
    }, [options.roomName, options.websocketUrl, options.uid]);

    // Create DB Group API
    const createDbGroup = useCallback((groupData) => {
        if (!instanceRef.current) return Promise.reject(new Error('Yjs not initialized'));
        const { ydoc } = instanceRef.current;
        const groupsMap = ydoc.getMap('dbgroups');

        const newGroup = {
            id: groupData.id || `dbg_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
            ...groupData
        };

        ydoc.transact(() => {
            groupsMap.set(newGroup.name, newGroup);
        }, REACT_ORIGIN);

        const list = [];
        groupsMap.forEach((val) => {
            list.push(val instanceof Y.Map ? yMapToObject(val) : val);
        });
        setDbGroups(list);

        // Also save to legacy IndexedDB if store exists
        if (typeof indexedDB !== 'undefined') {
            try {
                const req = indexedDB.open('ShepherdDB');
                req.onsuccess = (e) => {
                    const db = e.target.result;
                    if (db.objectStoreNames.contains('DBgroups')) {
                        const tx = db.transaction(['DBgroups'], 'readwrite');
                        tx.objectStore('DBgroups').put(newGroup);
                        tx.oncomplete = () => db.close();
                    } else {
                        db.close();
                    }
                };
            } catch (_) {}
        }

        return Promise.resolve(newGroup);
    }, []);

    // Delete DB Group API
    const deleteDbGroup = useCallback((groupName) => {
        if (!instanceRef.current) return;
        const { ydoc } = instanceRef.current;
        const groupsMap = ydoc.getMap('dbgroups');

        ydoc.transact(() => {
            groupsMap.delete(groupName);
        }, REACT_ORIGIN);

        const list = [];
        groupsMap.forEach((val) => {
            list.push(val instanceof Y.Map ? yMapToObject(val) : val);
        });
        setDbGroups(list);
    }, []);

    return {
        dbGroups,
        createDbGroup,
        deleteDbGroup,
        isHydrated,
        isConnected,
        isSynced,
        tableInstance: instanceRef.current
    };
}

/**
 * Custom Hook: useYjsDatabases
 * Manages database instances via Yjs and initializes window.Yjs.tables.<jsondb_name>.
 */
export function useYjsDatabases(options = {}) {
    const [databases, setDatabases] = useState([]);
    const [isHydrated, setIsHydrated] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isSynced, setIsSynced] = useState(false);
    const instanceRef = useRef(null);

    useEffect(() => {
        const table = initTable('databases', {
            roomName: options.roomName || 'shepherd-databases',
            uid: options.uid,
            websocketUrl: options.websocketUrl
        });
        instanceRef.current = table;
        const ydoc = table.ydoc;
        const dbsMap = ydoc.getMap('databases');

        const updateStateFromYjs = () => {
            const list = [];
            dbsMap.forEach((val) => {
                if (val && typeof val === 'object') {
                    list.push(val instanceof Y.Map ? yMapToObject(val) : val);
                }
            });
            setDatabases(list);
        };

        const observer = (_events, transaction) => {
            if (transaction.origin === REACT_ORIGIN) return;
            updateStateFromYjs();
        };

        dbsMap.observe(observer);

        if (table.indexeddbProvider) {
            table.indexeddbProvider.whenSynced.then(() => {
                setIsHydrated(true);
                updateStateFromYjs();

                if (dbsMap.size === 0 && typeof indexedDB !== 'undefined') {
                    try {
                        const req = indexedDB.open('ShepherdDB');
                        req.onsuccess = (e) => {
                            const db = e.target.result;
                            if (db.objectStoreNames.contains('Databases')) {
                                const tx = db.transaction(['Databases'], 'readonly');
                                const store = tx.objectStore('Databases');
                                const getAll = store.getAll();
                                getAll.onsuccess = () => {
                                    const legacyList = getAll.result || [];
                                    if (legacyList.length > 0 && dbsMap.size === 0) {
                                        ydoc.transact(() => {
                                            legacyList.forEach((database) => {
                                                if (database && database.name) {
                                                    dbsMap.set(database.name, database);
                                                }
                                            });
                                        }, REACT_ORIGIN);
                                        updateStateFromYjs();
                                    }
                                };
                                tx.oncomplete = () => db.close();
                            } else {
                                db.close();
                            }
                        };
                    } catch (_) {}
                }
            });
        }

        const handleStatus = (event) => {
            setIsConnected(event.status === 'connected');
        };

        const handleSync = (synced) => {
            setIsSynced(synced);
            if (synced) {
                updateStateFromYjs();
            }
        };

        if (table.websocketProvider) {
            table.websocketProvider.on('status', handleStatus);
            table.websocketProvider.on('sync', handleSync);
        }

        updateStateFromYjs();

        return () => {
            dbsMap.unobserve(observer);
            if (table.websocketProvider) {
                table.websocketProvider.off('status', handleStatus);
                table.websocketProvider.off('sync', handleSync);
            }
        };
    }, [options.roomName, options.websocketUrl, options.uid]);

    // Create Database API
    const createDatabase = useCallback((dbData) => {
        if (!instanceRef.current) return Promise.reject(new Error('Yjs not initialized'));
        const { ydoc } = instanceRef.current;
        const dbsMap = ydoc.getMap('databases');

        const newDb = {
            id: dbData.id || `db_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
            ...dbData
        };

        ydoc.transact(() => {
            dbsMap.set(newDb.name, newDb);
        }, REACT_ORIGIN);

        // Initialize table in window.Yjs.tables.<jsondb_name>
        initTable(newDb.name, {
            dbId: newDb.id,
            roomName: `db-${newDb.id}`,
            uid: options.uid
        });

        const list = [];
        dbsMap.forEach((val) => {
            list.push(val instanceof Y.Map ? yMapToObject(val) : val);
        });
        setDatabases(list);

        // Also save to legacy IndexedDB if store exists
        if (typeof indexedDB !== 'undefined') {
            try {
                const req = indexedDB.open('ShepherdDB');
                req.onsuccess = (e) => {
                    const db = e.target.result;
                    if (db.objectStoreNames.contains('Databases')) {
                        const tx = db.transaction(['Databases'], 'readwrite');
                        tx.objectStore('Databases').put(newDb);
                        tx.oncomplete = () => db.close();
                    } else {
                        db.close();
                    }
                };
            } catch (_) {}
        }

        return Promise.resolve(newDb);
    }, [options.uid]);

    return {
        databases,
        createDatabase,
        isHydrated,
        isConnected,
        isSynced,
        tableInstance: instanceRef.current
    };
}

/**
 * Custom Hook: useYjsRecords
 * Wraps Yjs `ydoc.getArray('records')` into synchronized React state.
 * Prevents re-render loops using REACT_ORIGIN.
 */
export function useYjsRecords(tableName, options = {}) {
    const [records, setRecords] = useState(options.initialRecords || []);
    const [isHydrated, setIsHydrated] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isSynced, setIsSynced] = useState(false);
    const [pendingChanges, setPendingChanges] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);

    const instanceRef = useRef(null);

    useEffect(() => {
        if (!tableName) return;

        const table = initTable(tableName, options);
        instanceRef.current = table;
        const ydoc = table.ydoc;
        const yArray = ydoc.getArray('records');

        // Observer for remote changes
        const observer = (_events, transaction) => {
            // Ignore transactions triggered locally by React state updaters
            if (transaction.origin === REACT_ORIGIN) {
                return;
            }
            const current = yArrayToArray(yArray);
            setRecords(current);
        };

        yArray.observeDeep(observer);

        // Hydration from IndexedDB
        if (table.indexeddbProvider) {
            table.indexeddbProvider.whenSynced.then(() => {
                setIsHydrated(true);
                const current = yArrayToArray(yArray);
                if (current.length > 0) {
                    setRecords(current);
                } else if (options.initialRecords && options.initialRecords.length > 0) {
                    // Populate from initial records if Y.Array is empty
                    populateYArray(yArray, options.initialRecords);
                    setRecords(yArrayToArray(yArray));
                }
            });
        }

        // WebSocket events
        const handleStatus = (event) => {
            setIsConnected(event.status === 'connected');
            if (event.status === 'connected') {
                setPendingChanges(false);
            }
        };

        const handleSync = (synced) => {
            setIsSynced(synced);
            if (synced) {
                setPendingChanges(false);
                setLastSyncTime(Date.now());
                const current = yArrayToArray(yArray);
                setRecords(current);
            }
        };

        if (table.websocketProvider) {
            table.websocketProvider.on('status', handleStatus);
            table.websocketProvider.on('sync', handleSync);
        }

        // Initial sync from existing Y.Array
        const existing = yArrayToArray(yArray);
        if (existing.length > 0) {
            setRecords(existing);
        }

        return () => {
            yArray.unobserveDeep(observer);
            if (table.websocketProvider) {
                table.websocketProvider.off('status', handleStatus);
                table.websocketProvider.off('sync', handleSync);
            }
        };
    }, [tableName, options.roomName, options.websocketUrl, options.uid]);

    // Add Record
    const addRecord = useCallback((newRecord) => {
        if (!instanceRef.current) return;
        const { ydoc } = instanceRef.current;
        const yArray = ydoc.getArray('records');

        const yMap = new Y.Map();
        Object.entries(newRecord).forEach(([k, v]) => yMap.set(k, v));

        ydoc.transact(() => {
            yArray.push([yMap]);
        }, REACT_ORIGIN);

        setRecords(yArrayToArray(yArray));
    }, []);

    // Update Record at index
    const updateRecord = useCallback((index, updatedFields) => {
        if (!instanceRef.current) return;
        const { ydoc } = instanceRef.current;
        const yArray = ydoc.getArray('records');

        if (index < 0 || index >= yArray.length) return;

        ydoc.transact(() => {
            const item = yArray.get(index);
            if (item instanceof Y.Map) {
                Object.entries(updatedFields).forEach(([k, v]) => {
                    item.set(k, v);
                });
            } else {
                const yMap = new Y.Map();
                Object.entries(updatedFields).forEach(([k, v]) => yMap.set(k, v));
                yArray.delete(index, 1);
                yArray.insert(index, [yMap]);
            }
        }, REACT_ORIGIN);

        setRecords(yArrayToArray(yArray));
    }, []);

    // Delete Record at index
    const deleteRecord = useCallback((index) => {
        if (!instanceRef.current) return;
        const { ydoc } = instanceRef.current;
        const yArray = ydoc.getArray('records');

        if (index < 0 || index >= yArray.length) return;

        ydoc.transact(() => {
            yArray.delete(index, 1);
        }, REACT_ORIGIN);

        setRecords(yArrayToArray(yArray));
    }, []);

    // Bulk set / seed records
    const setAllRecords = useCallback((newRecords) => {
        if (!instanceRef.current) return;
        const { ydoc } = instanceRef.current;
        const yArray = ydoc.getArray('records');

        populateYArray(yArray, newRecords);
        setRecords(yArrayToArray(yArray));
    }, []);

    return {
        records,
        setRecords: setAllRecords,
        addRecord,
        updateRecord,
        deleteRecord,
        isHydrated,
        isConnected,
        isSynced,
        pendingChanges,
        lastSyncTime,
        tableInstance: instanceRef.current
    };
}

/**
 * ============================================================================
 * Helper Functions (CRDT serialization, deep set/get)
 * ============================================================================
 */

export function yArrayToArray(yArray) {
    return yArray.toArray().map(item => {
        if (item instanceof Y.Map) {
            return yMapToObject(item);
        }
        return item;
    });
}

export function yMapToObject(yMap) {
    const obj = {};
    yMap.forEach((value, key) => {
        if (value instanceof Y.Map) {
            obj[key] = yMapToObject(value);
        } else if (value instanceof Y.Array) {
            obj[key] = yArrayToArray(value);
        } else {
            obj[key] = value;
        }
    });
    return obj;
}

export function populateYArray(yArray, array) {
    const doc = yArray.doc;
    if (!doc) return;

    doc.transact(() => {
        yArray.delete(0, yArray.length);
        array.forEach(item => {
            if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
                const yMap = new Y.Map();
                Object.entries(item).forEach(([key, value]) => {
                    yMap.set(key, value);
                });
                yArray.push([yMap]);
            } else {
                yArray.push([item]);
            }
        });
    }, REACT_ORIGIN);
}

export function setDeepYjs(root, path, value) {
    if (!path || path.length === 0) return;

    let current = root;

    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        const nextKey = path[i + 1];
        let nextNode = getChild(current, key);

        if (!nextNode) {
            nextNode = typeof nextKey === 'number' ? new Y.Array() : new Y.Map();
            setChild(current, key, nextNode);
        }

        current = nextNode;
    }

    const lastKey = path[path.length - 1];
    const yValue = toYjsType(value);
    setChild(current, lastKey, yValue);
}

function getChild(parent, key) {
    if (parent instanceof Y.Map) {
        return parent.get(key);
    } else if (parent instanceof Y.Array) {
        return parent.get(key);
    }
    throw new Error(`Unsupported Yjs parent type at key: ${key}`);
}

function setChild(parent, key, value) {
    if (parent instanceof Y.Map) {
        parent.set(key, value);
    } else if (parent instanceof Y.Array) {
        if (key >= parent.length) {
            parent.insert(key, [value]);
        } else {
            parent.delete(key, 1);
            parent.insert(key, [value]);
        }
    }
}

function toYjsType(val) {
    if (val !== null && typeof val === 'object') {
        if (Array.isArray(val)) {
            const yArray = new Y.Array();
            yArray.insert(0, val.map(toYjsType));
            return yArray;
        } else if (val.constructor === Object) {
            const yMap = new Y.Map();
            for (const [k, v] of Object.entries(val)) {
                yMap.set(k, toYjsType(v));
            }
            return yMap;
        }
    }
    return val;
}

export function getDeepYjs(root, path, defaultValue = undefined) {
    if (!root || path === undefined || path === null) return defaultValue;

    const keys = Array.isArray(path)
        ? path
        : path.toString().split('.').map((k) => (/^\d+$/.test(k) ? Number(k) : k));

    let current = root;

    for (const key of keys) {
        if (current === undefined || current === null) {
            return defaultValue;
        }

        if (current instanceof Y.Doc) {
            current = current.getMap(key);
        } else if (current instanceof Y.Map) {
            current = current.get(key);
        } else if (current instanceof Y.Array) {
            if (typeof key !== 'number' || key < 0 || key >= current.length) {
                return defaultValue;
            }
            current = current.get(key);
        } else if (typeof current === 'object') {
            current = current[key];
        } else {
            return defaultValue;
        }
    }

    return current !== undefined ? current : defaultValue;
}

const yjsServices = {
    initializeYjsDocumentSession,
    initTable,
    getTable,
    destroyTable,
    useYjsTable,
    useYjsDbGroups,
    useYjsDatabases,
    useYjsRecords,
    yArrayToArray,
    yMapToObject,
    populateYArray,
    setDeepYjs,
    getDeepYjs,
    SyncStatus
};

export default yjsServices;
